import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import axios from "axios";
import logger from "../../utilitis/logger";

// Any failure counts as "no orders today" - the check must never block placing an order
const fetchTodaysOrders = async () => {
  try {
    const response = await axios.get("/nursinghome/todays-orders", { withCredentials: true });
    return response.data?.orders || [];
  } catch (error) {
    logger.error("Error checking today's orders - continuing with submission:", error);
    return [];
  }
};

/**
 * Asks the customer to confirm before placing an additional order on a day they already ordered.
 * - runCheck(onProceed): runs onProceed right away when there are no orders today,
 *   otherwise opens the dialog and runs it only if the customer confirms
 * - checking: true while the check is in progress (for the button spinner)
 * - dialogProps: spread onto <TodaysOrdersDialog />
 */
export const useTodaysOrdersCheck = () => {
  const [checking, setChecking] = useState(false);
  const [todaysOrders, setTodaysOrders] = useState([]);
  const pendingActionRef = useRef(null);

  const close = () => {
    setTodaysOrders([]);
    pendingActionRef.current = null;
  };

  const runCheck = async (onProceed) => {
    setChecking(true);
    const orders = await fetchTodaysOrders();

    if (orders.length === 0) {
      onProceed(); // sets `submitting` before its first await, so the spinner never flickers
      setChecking(false);
      return;
    }

    pendingActionRef.current = onProceed;
    setTodaysOrders(orders);
    setChecking(false);
  };

  const handleConfirm = () => {
    const proceed = pendingActionRef.current;
    close();
    proceed?.();
  };

  return {
    runCheck,
    checking,
    dialogProps: {
      orders: todaysOrders,
      onConfirm: handleConfirm,
      onCancel: close,
    },
  };
};

const TodaysOrdersDialog = ({ orders, onConfirm, onCancel }) => (
  <Dialog open={orders.length > 0} onClose={onCancel} maxWidth="sm" fullWidth scroll="paper">
    <DialogTitle sx={{ pb: 1, backgroundColor: "#fff3e0", color: "#e65100" }}>
      <Typography variant="h6" sx={{ fontWeight: 600, textAlign: "left" }} dir="rtl">
        שים לב
      </Typography>
    </DialogTitle>
    <DialogContent dividers dir="rtl">
      <Typography variant="body1" sx={{ textAlign: "left", fontWeight: 600 }}>
        כבר בוצעה היום הזמנה עבור בית האבות. בטבלה מוצגים הפריטים שהוזמנו בהזמנה הקודמת.
      </Typography>
      <Typography variant="body1" sx={{ textAlign: "left", fontWeight: 600, mb: 2 }}>
        האם לשלוח גם את ההזמנה הנוכחית?
      </Typography>

      {orders.map((order) => (
        <Box key={order.orderName} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ textAlign: "left", mb: 0.5 }}>
            הזמנה {order.orderName}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>מק"ט</TableCell>
                  <TableCell>תיאור</TableCell>
                  <TableCell align="center">כמות</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {order.items.map((item, index) => (
                  <TableRow key={`${item.partname}-${index}`}>
                    <TableCell>{item.partname}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell align="center">{item.quantity}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
      <Button onClick={onCancel} variant="contained" color="primary">
        ביטול
      </Button>
      <Button onClick={onConfirm} variant="contained" color="primary">
        אישור
      </Button>
    </DialogActions>
  </Dialog>
);

export default TodaysOrdersDialog;
